/**
 * localStorage key for the shared favourites store.
 *
 * Distinct from `metro:suggester:recents` — recents and favourites are
 * separate persisted lists.
 */
export const FAVORITES_KEY = 'metro:favorites' as const

/**
 * FavoritesService — the shared, cross-platform favourites store (spec §16).
 *
 * - **Written by** the host's StationDetails favourite toggle.
 * - **Read by** Suggester's `idle` Favorites section.
 *
 * The service contract (`favorite` / `unfavorite` / `toggle` / `isFavorite`
 * / `getAll`) is STRICT and identical across platforms; the storage
 * backend is platform-idiomatic — iOS Core Data, Android DataStore, Web
 * localStorage (this implementation). A future migration to IndexedDB is
 * transparent behind this interface.
 *
 * The service is observable so the Suggester Favorites section
 * re-renders live when StationDetails toggles a favourite.
 */
export interface FavoritesService {
  /** All favourited station ids, in insertion order (oldest first). */
  getAll: () => readonly string[]
  /** `true` when `id` is currently favourited. */
  isFavorite: (id: string) => boolean
  /** Add `id` to favourites. Idempotent. */
  favorite: (id: string) => void
  /** Remove `id` from favourites. Idempotent. */
  unfavorite: (id: string) => void
  /** Flip `id`'s membership; returns the resulting favourited state. */
  toggle: (id: string) => boolean
  /** Subscribe to change notifications. Returns an unsubscribe fn. */
  subscribe: (listener: () => void) => () => void
}

/**
 * Upper bound on persisted favourites (QA M-3). Unbounded growth would
 * eventually blow the localStorage quota and silently drop the whole
 * list on the next `persist`; capping keeps the newest N. Generous vs
 * the idle Favorites section's 8-row budget, but bounded.
 */
export const FAVORITES_CAP = 100

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((e) => typeof e === 'string')
}

function loadFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY)
    if (raw === null) return []
    const parsed = JSON.parse(raw) as unknown
    return isStringArray(parsed) ? parsed.slice(-FAVORITES_CAP) : []
  } catch {
    return []
  }
}

function persist(ids: readonly string[]): void {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids))
  } catch {
    // localStorage may be unavailable (private mode, quota). Drop silently —
    // the in-memory list still works for the current session.
  }
}

/**
 * Build a {@link FavoritesService} backed by `localStorage`.
 *
 * Hydrates from any previously persisted list on creation; a malformed
 * payload is treated as empty. Every mutation persists best-effort and
 * notifies subscribers (only when membership actually changed).
 */
export function createFavoritesService(): FavoritesService {
  let ids: string[] = loadFavorites()
  // Frozen immutable snapshot returned by `getAll` (QA M-4). It is a
  // copy of `ids` (callers can't mutate internal state) AND a STABLE
  // reference between mutations — recreated only in `commit`. The stable
  // identity is load-bearing: AppShell reads favourites via
  // `useSyncExternalStore(subscribe, getAll)`, whose `getSnapshot` MUST
  // return the same reference when nothing changed, or React loops
  // forever (error #185). A fresh `[...ids]` per call would do exactly
  // that.
  let snapshot: readonly string[] = Object.freeze([...ids])
  const listeners = new Set<() => void>()

  const notify = (): void => {
    for (const l of listeners) l()
  }

  const commit = (next: string[]): void => {
    // Enforce the cap on every write (QA M-3) — keep the newest entries.
    ids = next.length > FAVORITES_CAP ? next.slice(-FAVORITES_CAP) : next
    snapshot = Object.freeze([...ids])
    persist(ids)
    notify()
  }

  return {
    // Stable frozen snapshot — same ref between mutations (M-4 + #185).
    getAll: () => snapshot,
    isFavorite: (id) => ids.includes(id),
    favorite: (id) => {
      if (ids.includes(id)) return
      commit([...ids, id])
    },
    unfavorite: (id) => {
      if (!ids.includes(id)) return
      commit(ids.filter((entry) => entry !== id))
    },
    toggle: (id) => {
      if (ids.includes(id)) {
        commit(ids.filter((entry) => entry !== id))
        return false
      }
      commit([...ids, id])
      return true
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/** Флаг одноразовой миграции ключа избранного station.id → naptanId. */
export const FAVORITES_NAPTAN_MIGRATED_KEY = 'metro:favorites:naptanMigrated' as const

/**
 * Одноразовая миграция сохранённого избранного со схемных `station.id` на
 * кластерные `naptanId` (спец §16: iOS Core Data и Android DataStore делают
 * то же самое своими флагами; контракт байт-идентичен).
 *
 * ИДЕМПОТЕНТНА: флаг проверяется ДО прохода. На втором вызове `resolve`
 * вернул бы `undefined` для уже мигрированной naptan-строки, и незащищённый
 * проход стёр бы всё избранное.
 *
 * Дубли кластера схлопываются, порядок (insertion order, oldest first)
 * сохраняется по ПЕРВОМУ вхождению. Неразрешимая строка удаляется:
 * восстановить её не из чего, и ни один экран её не покажет.
 */
export function migrateFavoritesToNaptan(
  service: FavoritesService,
  resolve: (stationId: string) => string | undefined,
): void {
  try {
    if (localStorage.getItem(FAVORITES_NAPTAN_MIGRATED_KEY) === '1') return
  } catch {
    // localStorage недоступен (private mode) — стор и так пуст на каждой
    // сессии, а без места под флаг повторный прогон стёр бы данные.
    // Не мигрируем вовсе.
    return
  }
  const current = service.getAll()
  const migrated: string[] = []
  const seen = new Set<string>()
  for (const id of current) {
    const naptanId = resolve(id)
    if (naptanId === undefined || seen.has(naptanId)) continue
    seen.add(naptanId)
    migrated.push(naptanId)
  }
  for (const id of current) service.unfavorite(id)
  for (const id of migrated) service.favorite(id)
  try {
    localStorage.setItem(FAVORITES_NAPTAN_MIGRATED_KEY, '1')
  } catch {
    // Записать флаг не удалось — ранний return выше уже исключил этот
    // случай (getItem бросил бы первым); ветка — страховка от асимметрии
    // квоты. Хуже не становится: прогон по naptan-строкам без флага стёр
    // бы список, но сюда мы попадаем только с работающим getItem.
  }
}
